import React, { useEffect, useState } from "react";
import { Cloud, Key, ShieldCheck, ShieldAlert, Save, Trash2, AlertCircle, Info, Flame, Upload, Box, Layers, ChevronDown, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function SuperSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({
    configured: false, aws: {
      access_key_masked: "", secret_key_masked: "",
      bucket: "", region: "", public_base: "",
    }, updated_at: "",
  });
  const [form, setForm] = useState({
    access_key: "", secret_key: "", bucket: "", region: "ap-south-1", public_base: "", cdn_domain: "",
  });

  // Initial mount load (inline to satisfy the static analyzer that
  // setState happens only in useEffect's own body)
  useEffect(() => {
    let active = true;
    api.get("/api/super-admin/settings/storage").then(({ data }) => {
      if (!active) return;
      setState(data);
      setForm({
        access_key: "", secret_key: "",
        bucket: data.aws?.bucket || "",
        region: data.aws?.region || "ap-south-1",
        public_base: data.aws?.public_base || "",
        cdn_domain: data.aws?.cdn_domain || "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { active = false; };
  }, []);

  // Re-fetch used by event handlers (save/clear). Separate from useEffect
  // so the linter doesn't classify it as "called from an effect".
  const refresh = async () => {
    const { data } = await api.get("/api/super-admin/settings/storage");
    setState(data);
    setForm({
      access_key: "", secret_key: "",
      bucket: data.aws?.bucket || "",
      region: data.aws?.region || "ap-south-1",
      public_base: data.aws?.public_base || "",
      cdn_domain: data.aws?.cdn_domain || "",
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      // Only send non-empty fields so masked values aren't overwritten
      const body = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v && v.trim() !== "")
      );
      await api.put("/api/super-admin/settings/storage", body);
      toast.success("Storage settings saved");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Disconnect AWS S3? Uploads will fall back to local storage.")) return;
    setBusy(true);
    try {
      await api.delete("/api/super-admin/settings/storage");
      toast.success("Storage disconnected");
      await refresh();
    } finally { setBusy(false); }
  };

  if (loading) return <LoadingState fullHeight />;

  return (
    <div className="space-y-8 page-enter max-w-4xl" data-testid="super-settings-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-brand-clay">Platform Owner · 9858558555</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Platform Settings</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Configure global services used by every tenant on DairyFresh.
        </p>
      </div>

      {/* AWS S3 */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-brand-sky" />
            <div>
              <h2 className="font-heading font-bold text-lg">AWS S3 Image Hosting</h2>
              <p className="text-xs text-neutral-500">Logos, banners, products, categories — all uploads from tenants are stored here.</p>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 ${
            state.configured ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-clay/10 text-brand-clay"
          }`} data-testid="aws-status-pill">
            {state.configured ? <><ShieldCheck className="w-3.5 h-3.5"/> Connected</> : <><ShieldAlert className="w-3.5 h-3.5"/> Not connected</>}
          </span>
        </div>

        {!state.configured && (
          <div className="rounded-2xl bg-brand-cream border border-brand-sand/40 p-4 mb-5 flex items-start gap-3">
            <Info className="w-4 h-4 text-brand-sand shrink-0 mt-0.5"/>
            <div className="text-sm text-brand-ink">
              Until you connect S3, tenant uploads are saved on the platform server. That works fine for dev/demo, but for production multi-tenant scale, plug in AWS keys below.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="AWS Access Key ID" hint={state.aws.access_key_masked ? `Saved: ${state.aws.access_key_masked}` : "Required"}>
            <Input value={form.access_key} onChange={(e) => setForm({ ...form, access_key: e.target.value })}
                   placeholder={state.aws.access_key_masked ? "Leave blank to keep current" : "AKIA…"}
                   className="h-12 rounded-2xl font-mono text-sm" data-testid="aws-access-key-input"/>
          </Field>
          <Field label="AWS Secret Access Key" hint={state.aws.secret_key_masked ? `Saved: ${state.aws.secret_key_masked}` : "Required"}>
            <Input type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                   placeholder={state.aws.secret_key_masked ? "Leave blank to keep current" : "••••••••"}
                   className="h-12 rounded-2xl font-mono text-sm" data-testid="aws-secret-key-input"/>
          </Field>
          <Field label="S3 Bucket Name">
            <Input value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                   placeholder="dairyfresh-images"
                   className="h-12 rounded-2xl font-mono text-sm" data-testid="aws-bucket-input"/>
          </Field>
          <Field label="Region">
            <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
                   placeholder="ap-south-1"
                   className="h-12 rounded-2xl font-mono text-sm" data-testid="aws-region-input"/>
          </Field>
          <Field label="Public Base URL (optional)" hint="Direct S3 / custom domain. Used only if CDN domain below is empty.">
            <Input value={form.public_base} onChange={(e) => setForm({ ...form, public_base: e.target.value })}
                   placeholder="https://dairyfresh-images.s3.ap-south-1.amazonaws.com"
                   className="h-12 rounded-2xl font-mono text-sm md:col-span-2" data-testid="aws-public-base-input"/>
          </Field>
          <Field label="CloudFront CDN Domain (recommended)" hint="Reads go through this. Falls back to public_base then raw S3.">
            <Input value={form.cdn_domain} onChange={(e) => setForm({ ...form, cdn_domain: e.target.value })}
                   placeholder="dxxxxxxxxxxxxx.cloudfront.net"
                   className="h-12 rounded-2xl font-mono text-sm md:col-span-2" data-testid="aws-cdn-input"/>
          </Field>
        </div>

        <div className="flex gap-3 mt-6 flex-wrap">
          <Button onClick={save} disabled={busy}
                  className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-7"
                  data-testid="save-aws-btn">
            <Save className="w-4 h-4 mr-2"/> Save AWS keys
          </Button>
          {state.configured && (
            <Button onClick={clear} disabled={busy} variant="outline"
                    className="h-12 rounded-2xl border-2 border-brand-clay text-brand-clay font-bold px-7"
                    data-testid="clear-aws-btn">
              <Trash2 className="w-4 h-4 mr-2"/> Disconnect
            </Button>
          )}
          {state.updated_at && (
            <span className="text-xs text-neutral-500 self-center">Last updated {new Date(state.updated_at).toLocaleString()}</span>
          )}
        </div>

        <div className="mt-6 rounded-2xl bg-neutral-50 border border-neutral-200 p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-2">
            <Key className="w-3.5 h-3.5"/> IAM permissions required
          </div>
          <pre className="text-xs font-mono text-neutral-700 overflow-x-auto">{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": ["s3:PutObject", "s3:PutObjectAcl", "s3:GetObject"],
    "Resource": "arn:aws:s3:::YOUR-BUCKET/*"
  }]
}`}</pre>
          <div className="text-xs text-neutral-500 mt-2 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
            Make sure your bucket allows public reads on the uploaded objects (or set up CloudFront).
          </div>
        </div>
      </section>

      {/* Firebase service accounts */}
      <FirebaseSection/>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}


function FirebaseSection() {
  const [state, setState] = useState({
    projects: [], total_tenants_capacity_remaining: 0,
    apps_per_tenant: 4, max_apps_per_project: 30,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Initial mount load (inline to keep setState in useEffect's own body)
  useEffect(() => {
    let active = true;
    api.get("/api/super-admin/firebase").then(({ data }) => {
      if (!active) return;
      setState(data);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { active = false; };
  }, []);

  // Re-fetch used by event handlers (upload/delete). Separate so the linter
  // doesn't mark these as effect-reachable setState calls.
  const refresh = async () => {
    const { data } = await api.get("/api/super-admin/firebase");
    setState(data);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/api/super-admin/firebase/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onDelete = async (projectId) => {
    if (!window.confirm(`Remove Firebase project '${projectId}'? Tenants using it must be reassigned first.`)) return;
    try {
      await api.delete(`/api/super-admin/firebase/${projectId}`);
      toast.success("Removed");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const onSync = async (projectId) => {
    // Reconcile our local apps_count with the *actual* Firebase project state.
    // Use this when the dashboard says e.g. "28/30 used" but Firebase Console
    // shows fewer apps — typically caused by repeated re-provisioning of the
    // same tenant inflating the local counter.
    try {
      const { data } = await api.post(`/api/super-admin/firebase/${projectId}/sync`);
      const drift = data.drift_corrected;
      if (drift > 0) {
        toast.success(`Synced: corrected ${drift} ghost apps (was ${data.apps_count_before}, now ${data.apps_count_after})`);
      } else {
        toast.success(`Already in sync — ${data.apps_count_after} apps on Firebase`);
      }
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
  };

  return (
    <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="firebase-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Flame className="w-5 h-5 text-orange-500"/>
          <div>
            <h2 className="font-heading font-bold text-lg">Firebase Projects</h2>
            <p className="text-xs text-neutral-500">
              Each tenant gets {state.apps_per_tenant} Firebase apps (Customer iOS+Android, Rider iOS+Android).
              Up to {Math.floor(state.max_apps_per_project / state.apps_per_tenant)} tenants per project.
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Tenant capacity left</div>
          <div className="font-heading text-2xl font-extrabold text-brand-leaf" data-testid="fb-total-capacity">
            {state.total_tenants_capacity_remaining}
          </div>
        </div>
      </div>

      {/* Project list */}
      {loading ? (
        <LoadingState size="sm" />
      ) : state.projects.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-neutral-300" data-testid="fb-empty">
          <EmptyState icon={Flame} title="No Firebase projects uploaded yet"
                      description="Upload a service-account JSON below to enable push notifications + native app provisioning." />
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {state.projects.map((p) => {
            const pct = Math.round((p.apps_count / p.apps_capacity) * 100);
            return (
              <div key={p.project_id} className="rounded-2xl bg-brand-cream/40 border border-neutral-200 p-4"
                   data-testid={`fb-project-${p.project_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500"/>
                      <code className="font-mono font-bold text-sm">{p.project_id}</code>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 truncate">{p.client_email}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => onSync(p.project_id)}
                            className="text-brand-sky hover:bg-brand-sky/10 w-9 h-9 inline-flex items-center justify-center rounded-lg"
                            data-testid={`fb-sync-${p.project_id}`}
                            title="Sync apps_count with the actual Firebase project (fixes drift from repeated re-provisioning)">
                      <RefreshCw className="w-4 h-4"/>
                    </button>
                    <button onClick={() => onDelete(p.project_id)}
                            className="text-brand-clay hover:bg-brand-clay/10 w-9 h-9 inline-flex items-center justify-center rounded-lg"
                            data-testid={`fb-delete-${p.project_id}`}
                            title="Remove">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3 text-center">
                  <Stat label="Apps used" value={`${p.apps_count} / ${p.apps_capacity}`}/>
                  <Stat label="Tenant slots left" value={p.tenants_capacity_remaining}/>
                  <Stat label="Assigned tenants" value={p.tenant_assignments?.length || 0}/>
                </div>

                {/* Capacity bar */}
                <div className="mt-3 h-2 rounded-full bg-neutral-200 overflow-hidden">
                  <div className={`h-full ${pct > 80 ? "bg-brand-clay" : pct > 50 ? "bg-brand-sand" : "bg-brand-leaf"}`}
                       style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload form */}
      <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed cursor-pointer transition ${
        busy ? "opacity-50 pointer-events-none" : "border-brand-sky/40 hover:bg-brand-sky/5"
      }`}>
        <Upload className="w-5 h-5 text-brand-sky"/>
        <div className="flex-1">
          <div className="font-bold text-sm">Upload Firebase service-account JSON</div>
          <div className="text-xs text-neutral-500">
            Firebase Console → Project Settings → Service Accounts → Generate new private key
          </div>
        </div>
        <input type="file" accept=".json" onChange={onUpload} className="hidden" disabled={busy}
               data-testid="fb-upload-input"/>
        <span className="h-10 px-4 rounded-full bg-brand-sky text-white font-bold text-xs inline-flex items-center gap-1.5">
          {busy ? "Uploading…" : "Choose file"}
        </span>
      </label>

      <details className="mt-4 text-xs text-neutral-600">
        <summary className="font-bold cursor-pointer inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5"/> Required service-account role <ChevronDown className="w-3.5 h-3.5"/>
        </summary>
        <div className="mt-2 pl-5 space-y-1">
          <div>• <b>Firebase Admin</b> (provisions Firebase apps)</div>
          <div>• <b>Firebase Project Editor</b> (for app config download)</div>
          <div>• <b>Service Usage Consumer</b> (calls Firebase Management API)</div>
        </div>
      </details>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading font-bold text-base mt-0.5">{value}</div>
    </div>
  );
}

